import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { APP_STORE_CONFIG_TOKEN, ApplicationPathConstants } from '@ddt-web/config';
import { AppStoreConfig, DisplayDataFactoryService } from '@ddt-web/data-service';
import { EngagementPortfolio } from '@ddt-web/domain';
import { BpasEngagement } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import * as castArray from 'lodash/castArray';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, first, map, tap } from 'rxjs/operators';

import { PasDataLoaderService } from '../pas-setup-data-workers/services/pas-data-loader.service';
import { EngagementResolverWorker } from './engagement-resolver-worker';

@Injectable()
export class BpasEngagementResolverWorker extends EngagementResolverWorker {
	private routeForAnalysis: Observable<string>;

	constructor(
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig,
		protected displayDataService: DisplayDataFactoryService,
		protected dataLoader: PasDataLoaderService,
		protected storeService: StoreService,
		protected router: Router
	) {
		super(storeConfig, dataLoader, displayDataService, storeService);
	}

	public handle(queryParams: any): Subscription {
		if (!isEmpty(castArray(queryParams?.engagement ?? []))) {

			if (!this.routeForAnalysis) {
				this.routeForAnalysis = this.router.events.pipe(
					filter(event => event instanceof NavigationEnd),
					map((event: NavigationEnd) => event.urlAfterRedirects)
				);
			}

			return this.setupEngagement(queryParams);
		}
	}

	public setupEngagement(queryParams): Subscription {
		return combineLatest(
			this.storeService.selectAll(this.storeConfig.engagementPortfolios).pipe(
				filter((engagementPortfolios: any) => engagementPortfolios && !engagementPortfolios.isLoading && engagementPortfolios.entities),
				distinctUntilChanged((previous, current) => get(current, `entities[${queryParams.engagement}]`) === get(previous, `entities[${queryParams.engagement}]`))
			),
			this.storeService.selectAll(this.storeConfig.associatedPortfolios).pipe(distinctUntilChanged()),
			this.routeForAnalysis
		).pipe(
			filter(([engagementPortfolios, associatedPortfolios, routeForAnalysis]: any) => !!routeForAnalysis && routeForAnalysis.includes(ApplicationPathConstants.BPAS_ANALYSIS_PATH))
			// eslint-disable-next-line
		).subscribe(([engagementPortfolios, associatedPortfolios]) => {
			// engagementPortfolios has been changed, but we need to make sure associatedPortfolios has the latest portfolios. If it doesnt, update associatedPortfolios.
			this.loadEngagementData(queryParams, engagementPortfolios, null, associatedPortfolios);
		});
	}

	public conditionalTriggerForDataLoader(engagementId: string): void {
		combineLatest(
			this.storeService.selectAll(this.storeConfig.engagementPortfolios),
			this.storeService.selectAll(this.storeConfig.associatedPortfolios)
		).pipe(
			first(
				([engagementPortfolios, associatedPortfolios]: any) => {
					const engagementEntities: BpasEngagement[] = engagementPortfolios.entities;
					const currentEngagement: BpasEngagement = engagementEntities[engagementId];
					const isEngagementStorePopulated: boolean = Boolean(engagementPortfolios && !engagementPortfolios.isLoading && engagementEntities);

					return isEngagementStorePopulated && this.areUpdatedEngagementsInAssociatedPortfolios(currentEngagement, associatedPortfolios, null);
				}
			),
			tap(([engagementPortfolios, associatedPortfolios]) => {
				const portfolioIds = get(engagementPortfolios, `entities[${engagementId}].children`, []).map(portfolio => portfolio.uniqueId);
				this.dataLoader.populateStoresForPositions(portfolioIds, true);
			})
		).subscribe(([engagementPortfolios, associatedPortfolios]) => {
			// engagementPortfolios and associatedPortfolios are both upto date, therefore we can fire the required portfolio-analysis calls.
			this.setDataLoader(engagementId, engagementPortfolios, null, associatedPortfolios);
		});
	}

	private areAllPortfoliosAdded(engagement, associatedPortfolios): boolean {
		return engagement.children.every(engagementPortfolio => associatedPortfolios.entities[engagementPortfolio.uniqueId]);
	}

	// Engagement portfolios for bpas are editable, so check the weights for securities as well as existence within associated portfolios
	protected areUpdatedEngagementsInAssociatedPortfolios(engagement: BpasEngagement, associatedPortfolios, configEntities): boolean {
		// Checks if associatedPortfolios has been populated
		let havePortfolioBeenUpdated = this.areAllPortfoliosAdded(engagement, associatedPortfolios);

		if (havePortfolioBeenUpdated) {
			// Checks if values in associatedPortfolios are upto date based on the portfolios within the engagement
			havePortfolioBeenUpdated = engagement.children.every(engagementPortfolio => {
				const associatedPortfolio = get(associatedPortfolios, `entities[${engagementPortfolio.uniqueId}]`, {});
				const mappedEngagementPortfolio: EngagementPortfolio = this.mapEngagementPortfolioToAssociatedPortfolio(engagementPortfolio.uniqueId, engagementPortfolio);
				const equality = this.arePortfoliosEqualForAnalysis(mappedEngagementPortfolio, associatedPortfolio);
				return equality;
			});
		}

		return havePortfolioBeenUpdated;
	}

	protected mapEngagementPortfolioToAssociatedPortfolio(id: string, engagementPortfolio): EngagementPortfolio {
		return {
			id: id,
			uniqueId: id,
			description: engagementPortfolio.description,
			dateLastModified: engagementPortfolio.dateLastModified,
			parentId: engagementPortfolio.parentId,
			allowedPermsForCurrentUser: engagementPortfolio.allowedPermsForCurrentUser,
			portfolioType: engagementPortfolio.type,
			subType: engagementPortfolio.subType,
			positions: engagementPortfolio.children
		} as EngagementPortfolio;
	}

	private arePortfoliosEqualForAnalysis(mappedEngagementPortfolio: EngagementPortfolio, associatedPortfolio: EngagementPortfolio): boolean {
		// Do not care if modifiedDate, portOrder and other attributes change as these dont affect analysis
		const areNameEqual = mappedEngagementPortfolio.description === associatedPortfolio.description;
		const areSubTypesEqual = mappedEngagementPortfolio.subType === associatedPortfolio.subType;

		const areSecurityWeightsEqual = get(mappedEngagementPortfolio, 'positions', []).map(engagementSecurity => {
			return associatedPortfolio.positions.some(associatedSecurity =>
				engagementSecurity.identifier === associatedSecurity.identifier &&
				engagementSecurity.weight === associatedSecurity.weight
			);
		}).every(securityEqualityResult => !!securityEqualityResult);

		return areNameEqual && areSubTypesEqual && areSecurityWeightsEqual;
	}
}
